/**
 * ExportElevationTab
 *
 * Elevation picker list used in the Export dialog's "Elevations" and "Both" tabs.
 * Renders a "Select all" checkbox and a per-elevation checkbox list, with an
 * empty-state message when no elevations are available.
 */

import { Checkbox } from '@/components/ui/checkbox';
import type { ElevationListItem } from '@/hooks/useElevationsByFloorPlan';

interface ExportElevationTabProps {
  elevations: ElevationListItem[];
  selectedIds: Set<string>;
  onChange: (ids: Set<string>) => void;
  disabled?: boolean;
}

/**
 * Build a display name for an elevation:
 * e.g. "Wall A — North (Living Room)" or "Wall 3" as fallback.
 */
function getElevationDisplayName(el: ElevationListItem): string {
  const base = el.name ?? el.wall_identifier ?? `Wall ${el.wall_index}`;
  const room = (el.rooms as { name: string | null } | null)?.name;
  return room ? `${base} (${room})` : base;
}

export function ExportElevationTab({
  elevations,
  selectedIds,
  onChange,
  disabled = false,
}: ExportElevationTabProps) {
  const allSelected = elevations.length > 0 && selectedIds.size === elevations.length;
  const someSelected = selectedIds.size > 0 && selectedIds.size < elevations.length;

  const handleSelectAll = (checked: boolean | 'indeterminate') => {
    if (checked === true) {
      onChange(new Set(elevations.map(e => e.id)));
    } else {
      onChange(new Set());
    }
  };

  const handleRowToggle = (id: string, checked: boolean | 'indeterminate') => {
    const next = new Set(selectedIds);
    if (checked === true) {
      next.add(id);
    } else {
      next.delete(id);
    }
    onChange(next);
  };

  if (elevations.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-sm text-muted-foreground">No elevations in this project</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      {/* Select all row */}
      <label className="flex items-center gap-2 py-2 cursor-pointer select-none">
        <Checkbox
          checked={allSelected ? true : someSelected ? 'indeterminate' : false}
          onCheckedChange={handleSelectAll}
          disabled={disabled}
          aria-label="Select all elevations"
        />
        <span className="text-sm font-medium">Select all</span>
        <span className="text-xs text-muted-foreground ml-auto">
          {selectedIds.size} / {elevations.length} selected
        </span>
      </label>

      <div className="border-t" />

      {/* Scrollable per-elevation list */}
      <div className="max-h-[400px] overflow-y-auto">
        {elevations.map(el => (
          <label
            key={el.id}
            className="flex items-center gap-2 py-2 border-b last:border-b-0 cursor-pointer select-none hover:bg-muted/30 rounded-sm px-1"
          >
            <Checkbox
              checked={selectedIds.has(el.id)}
              onCheckedChange={checked => handleRowToggle(el.id, checked)}
              disabled={disabled}
              aria-label={`Select ${getElevationDisplayName(el)}`}
            />
            <span className="text-sm">{getElevationDisplayName(el)}</span>
          </label>
        ))}
      </div>
    </div>
  );
}
